import { isEqual, pick, sortBy } from 'lodash';
import { useSelector } from 'react-redux';
import { CodeNameModel } from 'components/common/types';
import { NetworkCriteriaType, NetworkRangeConstraints } from 'components/common/types/DirectSalesNetworks.types';
import { UseNetworkTarget } from 'components/common/types/UseNetworkTarget.types';
import { getRangeErrors } from 'components/pages/PCM/Products/utils/validateRangeValues';
import { getPublisherTargetCriteria } from 'store/publisher/selectors';

const EDITABLE_FIELDS: (keyof NetworkCriteriaType)[] = [
  'code',
  'name',
  'include',
  'target',
  'priority',
  'greenTolerance',
  'orangeTolerance',
];

const isSelectedAndSavedOptionsEqual = (
  selectedOptions: NetworkCriteriaType[],
  savedNetworks: NetworkCriteriaType[],
): boolean => {
  const sortedSelected = sortBy(selectedOptions, 'code').map((o) => pick(o, EDITABLE_FIELDS));
  const sortedSaved = sortBy(savedNetworks, 'code').map((o) => pick(o, EDITABLE_FIELDS));
  return isEqual(sortedSelected, sortedSaved);
};

const useNetworkTarget = ({
  selectedOptions,
  setSelectedOptions,
  savedNetworks,
  rangeConstraints,
}: {
  selectedOptions: NetworkCriteriaType[];
  setSelectedOptions: (options: NetworkCriteriaType[]) => void;
  savedNetworks: NetworkCriteriaType[];
  rangeConstraints?: NetworkRangeConstraints;
}): UseNetworkTarget => {
  const {
    priority: defaultPriority,
    greenTolerance: defaultGreenTolerance,
    orangeTolerance: defaultOrangeTolerance,
  } = useSelector(getPublisherTargetCriteria);
  const isChange = !isSelectedAndSavedOptionsEqual(selectedOptions, savedNetworks);
  const isChangeValid = selectedOptions.every(({ target, minValue, maxValue, defaultValue }) => {
    const hasRangeConstraint = minValue !== undefined || maxValue !== undefined;
    if (hasRangeConstraint && defaultValue === undefined) return true;

    return !!target;
  });
  const isToleranceError = selectedOptions.some(
    ({ greenTolerance, orangeTolerance }) => greenTolerance > orangeTolerance,
  );

  const isRangeValuesError = selectedOptions.some(({ defaultValue, minValue, maxValue }) => {
    if (!minValue && !maxValue) return true;

    return getRangeErrors(defaultValue, minValue, maxValue).isAnyTargetValueError;
  });

  const isTargetOutOfRange = selectedOptions.some(({ target, minValue, maxValue }) => {
    if (!target || (!minValue && !maxValue)) return false;

    const numTarget = Number(target);
    if (minValue !== undefined && numTarget < minValue) return true;
    if (maxValue !== undefined && numTarget > maxValue) return true;

    return false;
  });

  const updateCurrentItemInputValues = ({
    networkCode,
    networkName,
    updatedValue,
  }: {
    networkCode: string;
    networkName: string;
    updatedValue: Partial<NetworkCriteriaType>;
  }): void => {
    const foundItemIndex = selectedOptions.findIndex((network) => network.code === networkCode);

    let updatedOptions: NetworkCriteriaType[];

    if (foundItemIndex === -1) {
      const constraints = rangeConstraints?.[networkCode];
      const newItem: NetworkCriteriaType = {
        code: networkCode,
        name: networkName,
        include: true,
        category: updatedValue.category ?? undefined,
        target: constraints?.defaultValue?.toString() ?? '1',
        priority: constraints?.priority ?? defaultPriority,
        greenTolerance: constraints?.greenTolerance ?? defaultGreenTolerance,
        orangeTolerance: constraints?.orangeTolerance ?? defaultOrangeTolerance,
        allocated: undefined,
        status: undefined,
        available: updatedValue.available,
        inNetwork: updatedValue.inNetwork,
        minValue: constraints?.minValue,
        maxValue: constraints?.maxValue,
        defaultValue: constraints?.defaultValue,
      };

      updatedOptions = [...selectedOptions, newItem];
    } else {
      const existingItem = selectedOptions[foundItemIndex];

      if (updatedValue.include === false) {
        updatedOptions = selectedOptions.filter((network) => network.code !== networkCode);
      } else {
        const updatedItem = {
          ...existingItem,
          ...updatedValue,
        };

        updatedOptions = [...selectedOptions];
        updatedOptions[foundItemIndex] = updatedItem;
      }
    }
    setSelectedOptions(updatedOptions);
  };

  return {
    priority: { code: '', name: '' },
    greenTolerance: 0,
    orangeTolerance: 0,
    handleValueChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { target: value?.toString() ?? '' },
      }),
    handlePriorityChange: (value: CodeNameModel, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { priority: value },
      }),
    handleGreenToleranceChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { greenTolerance: value },
      }),
    handleOrangeToleranceChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { orangeTolerance: value },
      }),
    handleSelectionToggle: (option: NetworkCriteriaType, category: string, isSelected: boolean) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { category, include: !isSelected, available: option.available, inNetwork: option.inNetwork },
      }),
    handleMinValueChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { minValue: value },
      }),
    handleMaxValueChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { maxValue: value },
      }),
    handleDefaultValueChange: (value: number, option: NetworkCriteriaType) =>
      updateCurrentItemInputValues({
        networkCode: option.code,
        networkName: option.name,
        updatedValue: { defaultValue: value },
      }),
    isToleranceError,
    isChange,
    isChangeValid,
    selectedOptions,
    isRangeValuesError,
    isTargetOutOfRange,
  };
};

export default useNetworkTarget;
